import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { PencilIcon } from "@heroicons/react/24/outline";
import { PencilLineIcon } from "lucide-react";
import { PlusIcon } from "@heroicons/react/24/outline";
import Card from "../../../components/ui/Card";
import HoverActionsMenu from "../../../components/HoverActionsMenu";
import ConfirmDialog from "../../../dialog/ConfirmDialog";
import OctagonAlertIcon from "../../../icons/OctagonAlertIcon";

interface Variation {
  id: string;
  author: string;
  avatarUrl: string;
  timeLabel: string;
  title: string;
  titlePreview: string;
  description: string;
  descriptionPreview: string;
  icon?: React.ReactNode;
}

interface VariationsSectionProps {
  variations?: Variation[];
  onAddVariation?: () => void;
  onEditVariation?: (variationId: string) => void;
  onDeleteVariation?: (variationId: string) => void;
}

const VariationCard: React.FC<{
  variation: Variation;
  onEdit?: () => void;
  onDelete?: () => void;
}> = ({ variation, onEdit, onDelete }) => {
  return (
    <Card className="group relative overflow-hidden w-full h-full min-h-[120px]">
      {/* Hover Actions Menu */}
      <HoverActionsMenu
        onOpen={onEdit}
        onDelete={onDelete}
        className="!top-2 !right-2 !translate-y-0"
      />

      {/* Card Body */}
      <div className="flex flex-col gap-5 p-2">
        {/* Meta Section */}
        <div className="flex items-start gap-1 w-full">
          <div className="flex gap-1 items-center flex-1">
            {/* User Info */}
            <div className="flex gap-1 items-center">
              <img
                src={variation.avatarUrl}
                alt={variation.author}
                className="w-5 h-5 rounded-full flex-shrink-0"
              />
              <p className="text-sm font-medium text-gray-900 whitespace-nowrap">
                {variation.author}
              </p>
            </div>
            <p className="text-xs text-gray-600 whitespace-nowrap">{variation.timeLabel}</p>
          </div>
        </div>

        {/* Content Section */}
        <div className="flex flex-col gap-2 w-full">
          {/* Title */}
          <div className="flex gap-1 items-start w-full" title={variation.title}>
            <PencilLineIcon className="w-4 h-4 text-gray-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm font-semibold text-gray-900 flex-1">{variation.titlePreview}</p>
          </div>

          {/* Description */}
          <div className="text-xs text-gray-600 text-wrap">{variation.descriptionPreview}</div>
        </div>
      </div>
    </Card>
  );
};

export default function VariationsSection({
  variations = [],
  onAddVariation,
  onEditVariation,
  onDeleteVariation,
}: VariationsSectionProps) {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteClick = (variationId: string) => {
    setDeleteId(variationId);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteId || !onDeleteVariation) return;
    try {
      setDeleting(true);
      await onDeleteVariation(deleteId);
      setConfirmOpen(false);
      setDeleteId(null);
    } catch (error) {
      // Error handling can be added here if needed
      console.error("Failed to delete variation:", error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="w-full space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between pb-2">
          <h2 className="text-lg font-semibold text-gray-900">{t("Variations")}</h2>
          <button
            onClick={onAddVariation}
            className="inline-flex items-center gap-1.5 px-1.5 py-1.5 bg-white border border-gray-300 rounded-md shadow-xs hover:bg-gray-50 transition-colors"
          >
            <PlusIcon className="w-3 h-3 text-gray-900" />
            <span className="text-xs font-medium text-gray-900 whitespace-nowrap">
              {t("Add Variation")}
            </span>
          </button>
        </div>

        {/* Variations Grid */}
        {variations.length > 0 && (
          <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-3 w-full">
            {variations.map((variation) => (
              <VariationCard
                key={variation.id}
                variation={variation}
                onEdit={() => onEditVariation?.(variation.id)}
                onDelete={() => handleDeleteClick(variation.id)}
              />
            ))}
          </div>
        )}

        {/* Empty State */}
        {variations.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <PencilIcon className="w-8 h-8 text-gray-400 mb-2" />
            <p className="text-sm text-gray-600">{t("No variations yet")}</p>
            <button
              onClick={onAddVariation}
              className="mt-3 text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              {t("Add your first variation")}
            </button>
          </div>
        )}
      </div>

      <ConfirmDialog
        visible={confirmOpen}
        title={t("Delete variation?")}
        description={t("This action cannot be undone. The variation will be permanently deleted.")}
        alertIcon={<OctagonAlertIcon />}
        dangerBtn
        submitting={deleting}
        onClose={() => {
          if (!deleting) {
            setConfirmOpen(false);
            setDeleteId(null);
          }
        }}
        onSubmit={handleConfirmDelete}
        submitBtnLabel={t("Delete")}
      />
    </>
  );
}
